//JobDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchJobByCompanyName, fetchJobById, filterJobs, fetchActiveJobCount } from '../services/jobService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import HorizontalScroll from '../components/HorizontalScroll';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../index.css';

const taglines = ['Your Gateway to Career Growth.', 'Where Talent Meets Opportunity.'];

const JobDetails = () => {
  const { id: rawId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJobCount, setActiveJobCount] = useState(0);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [categoryJobs, setCategoryJobs] = useState([]);
  const [similarJobs, setSimilarJobs] = useState([]);



  // Clean the URL
  useEffect(() => {
    const cleanId = rawId.split('#')[0];
    if (cleanId !== rawId) {
      navigate(`/job/${cleanId}`, { replace: true });
    }
  }, [rawId, navigate]);

  // Rotate taglines
  useEffect(() => {
    const interval = setInterval(() => {
      setTaglineIndex(prev => (prev + 1) % taglines.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadJob = async () => {
      setLoading(true);
      try {
        // Main job data
        const [jobData, count] = await Promise.all([
          fetchJobById(rawId),
          fetchActiveJobCount()
        ]);

        for (const key in jobData) {
          if (typeof jobData[key] === 'string') {
            jobData[key] = jobData[key].replace(/\\n/g, '\n');
          }
        }
        jobData.attachmentUrl = jobData.pdfUrl || jobData.imgUrl || '';
        setJob(jobData);
        setActiveJobCount(count);

        // Fetch other jobs in parallel, but errors here shouldn't break the main job display

        // Fetch same-company jobs
        if (jobData.companyName) {
          fetchJobByCompanyName(jobData.companyName)
            .then(jobs => {
              const filtered = jobs.filter(j => j.id !== jobData.id);
              setRelatedJobs(filtered);
            })
            .catch(err => {
              console.error('Failed to fetch related company jobs:', err);
              setRelatedJobs([]); // fallback
            });
        }

        // Fetch same-category jobs
        const fetchSimilarJobs = async () => {
          if (jobData?.requiredField) {
            try {
              const filtered = await filterJobs({
                requiredField: jobData.requiredField,
                page: 0,
                size: 5 // Fetch more for horizontal scroll
              });

              // Optional: Exclude current job
              const categoryFiltered = filtered.filter(j => j.id !== jobData.id);
              setCategoryJobs(categoryFiltered);
            } catch (err) {
              console.error('Error fetching similar jobs:', err);
            }
          }
        };

        fetchSimilarJobs();



      } catch (error) {
        console.error('Failed to load main job details:', error);
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [rawId]);

  {/** 
  // Lazy load job data
  useEffect(() => {
    const loadJob = async () => {
      setLoading(true);
      try {
        const [jobData, count] = await Promise.all([
          fetchJobById(rawId),
          fetchActiveJobCount()
        ]);
        for (const key in jobData) {
          if (typeof jobData[key] === 'string') {
            jobData[key] = jobData[key].replace(/\\n/g, '\n');
          }
        }
        jobData.attachmentUrl = jobData.pdfUrl || jobData.imgUrl || '';
        setJob(jobData);
        setActiveJobCount(count);

        // 🔍 Fetch other jobs from the same company
        if (jobData.companyName) {
          const jobsFromCompany = await fetchJobByCompanyName(jobData.companyName);
          const filtered = jobsFromCompany.filter(j => j.id !== jobData.id);
          setRelatedJobs(filtered);
        }

        if (jobData.requiredField) {
          const jobsInCategory = await filterJobs(jobData.requiredField);
          const filteredCatJobs = jobsInCategory.filter(j => j.id !== jobData.id).slice(0, 5);
          setCategoryJobs(filteredCatJobs);
        }

      } catch (error) {
        console.error('Failed to load job:', error);
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [rawId]);
*/}
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const getDaysRemainingLabel = (closingDate) => {
    const today = new Date();
    const close = new Date(closingDate);
    const diffTime = close - today;
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: 'Expired', color: '#d32f2f' };
    if (days <= 3) return { label: `${days} day${days === 1 ? '' : 's'} left`, color: '#fbc02d' };
    return { label: `${days} days left`, color: '#388e3c' };
  };

  const status = job ? getDaysRemainingLabel(job.closingDate) : null;

  const jobTitleTag = job ? `${job.title} at ${job.companyName} | Current Jobs in Kenya` : 'Job Details';
  const jobMetaDescription = job
    ? `${job.title} at ${job.companyName} in ${job.location}. Apply by ${formatDate(job.closingDate)}.`
    : 'Browse job openings in Kenya.';

  const structuredData = job
    ? {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: `${job.description || ''}\n\n${job.jobResponsibilities || ''}\n\n${job.requirements || ''}`,
      datePosted: new Date(job.postedDate || new Date()).toISOString(),
      validThrough: new Date(job.closingDate).toISOString(),
      employmentType: job.jobType || "Full-time",
      hiringOrganization: {
        "@type": "Organization",
        name: job.companyName || "Not Specified",
        sameAs: "https://currentjobs.co.ke"
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: job.location || "Kenya",
          addressCountry: "KE"
        }
      },
      baseSalary: job.salaryRange
        ? {
          "@type": "MonetaryAmount",
          currency: "KES",
          value: {
            "@type": "QuantitativeValue",
            value: parseInt(job.salaryRange.replace(/\D/g, '')) || 0,
            unitText: "MONTH"
          }
        }
        : undefined
    }
    : {};

  return (
    <>
      <Helmet>
        <title>{jobTitleTag}</title>
        <meta name="description" content={jobMetaDescription} />
        <meta name="robots" content="index, follow" />
        {job && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      <Header />

      <main style={styles.mainContent}>
        <div className="job-content-wrapper" style={styles.contentWrapper}>
          <div className="job-left" style={styles.leftColumn}>
            {loading ? (
              <div style={styles.loadingContainer}>
                <div className="skeleton title" />
                <div className="skeleton line" />
                <div className="skeleton block" />
                <div className="skeleton block" />
              </div>
            ) : !job ? (
              <div style={styles.errorContainer}>
                <h2>Job Not Found</h2>
                <p>The job may have expired or been removed.</p>
                <button onClick={() => navigate('/')} style={styles.homeButton}>
                  🏠 Return to Job Listings
                </button>
              </div>
            ) : (
              <div style={styles.jobContainer}>
                <h2>{job.title} - {job.companyName}</h2>
                {/** {job.companyName && <p><strong>Company:</strong> {job.companyName}</p>}
                {job.location && <p><strong>Location:</strong> {job.location}</p>}
                {job.requiredField && <p><strong>Field:</strong> {job.requiredField}</p>}
                {job.postedDate && <p><strong>Posted:</strong> {formatDate(job.postedDate)}</p>}
                {job.closingDate && (
                  <p>
                    <strong>Closing Date:</strong> {formatDate(job.closingDate)}
                    <span style={{ ...styles.statusBadge, backgroundColor: status.color }}>
                      {status.label}
                    </span>
                  </p>
                )}
                {job.jobType && <p><strong>Job Type:</strong> {job.jobType}</p>}
                {job.educationLevel && <p><strong>Education:</strong> {job.educationLevel}</p>}
                {job.experienceLevel && <p><strong>Experience:</strong> {job.experienceLevel}</p>}
                {job.salaryRange && <p><strong>Salary:</strong> {job.salaryRange}</p>}
               */
                }
                <div style={styles.metaGrid}>
                  {job.companyName && <div><strong>Company:</strong> {job.companyName}</div>}
                  {job.location && <div><strong>Location:</strong> {job.location}</div>}
                  {job.requiredField && <div><strong>Field:</strong> {job.requiredField}</div>}
                  {job.postedDate && <div><strong>Posted:</strong> {formatDate(job.postedDate)}</div>}
                  {job.closingDate && (
                    <div>
                      <strong>Closing Date:</strong> {formatDate(job.closingDate)}
                      <span style={{ ...styles.statusBadge, backgroundColor: status.color }}>
                        {status.label}
                      </span>
                    </div>
                  )}
                  {job.jobType && <div><strong>Job Type:</strong> {job.jobType}</div>}
                  {job.educationLevel && <div><strong>Education:</strong> {job.educationLevel}</div>}
                  {job.experienceLevel && <div><strong>Experience:</strong> {job.experienceLevel}</div>}
                  {job.salaryRange && <div><strong>Salary:</strong> {job.salaryRange}</div>}
                </div>

                {job.description && <MarkdownSection title="Description" content={job.description} />}
                {job.jobResponsibilities && <MarkdownSection title="Responsibilities" content={job.jobResponsibilities} />}
                {job.requirements && <MarkdownSection title="Requirements" content={job.requirements} />}
                {job.companyDetails && <MarkdownSection title="Company Details" content={job.companyDetails} />}
                {job.applicationMethod && <MarkdownSection title="Application Method" content={job.applicationMethod} />}

                <div style={styles.section}>
                  <h3>📎 Attachment</h3>
                  {job.attachmentUrl ? (
                    job.attachmentUrl.toLowerCase().endsWith('.pdf') ? (
                      <a href={job.attachmentUrl} target="_blank" rel="noopener noreferrer" style={styles.pdfLink}>
                        📄 {job.attachmentUrl.split('/').pop()}
                      </a>
                    ) : (
                      <img
                        src={job.attachmentUrl}
                        alt="Job Attachment"
                        style={styles.image}
                        onClick={() => window.open(job.attachmentUrl, '_blank')}
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    )
                  ) : (
                    <p style={styles.noneText}>None</p>
                  )}
                </div>
                {relatedJobs.length > 0 ? (
                  <div style={{ ...styles.section, marginTop: 40 }}>
                    <h3>🧑‍💼 More Jobs at {job.companyName}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
                      {relatedJobs.map((related) => (
                        <JobCard key={related.id} job={related} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ ...styles.section, marginTop: 40 }}>
                    <h3>📂 More Jobs at {job.companyName}</h3>
                    <p style={styles.noneText}>🚫 Not available</p>
                  </div>
                )}

                {categoryJobs.length > 0 ? (
                  <div style={{ ...styles.section, marginTop: 40 }}>
                    <h3>📂 More in {job.requiredField}</h3>
                    <HorizontalScroll>
                      {categoryJobs.map(categoryJob => (
                        <div key={categoryJob.id} style={{ flex: '0 0 auto' }}>
                          <JobCard job={categoryJob} />
                        </div>
                      ))}

                      {/* "View More" Card */}
                      <div
                        style={{
                          flex: '0 0 auto',
                          width: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e0f2f1',
                          border: '2px solod #00796b',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          padding: '12px 16px',
                          fontSize: '14px',
                          color: '#004d40',
                          fontWeight: '500',
                          transition: 'background-color 0.3s ease',
                          gap: '6px',
                          textAlign: 'center'
                        }}
                        onClick={() => navigate(`/?category=requiredField&value=${encodeURIComponent(job.requiredField)}`)}
                      >
                        <span role="img" aria-label="more">🔎</span>&nbsp;<strong>View More</strong>
                      </div>
                    </HorizontalScroll>
                  </div>
                ) : (
                  <div style={{ ...styles.section, marginTop: 40 }}>
                    <h3>📂 More Jobs in {job.requiredField}</h3>
                    <p style={styles.noneText}>🚫 Not available</p>
                  </div>
                )}


              </div>
            )}
          </div>

          {/* Right Sidebar - Always visible */}
          <div className="job-right" style={styles.rightColumn}>
            <div style={styles.sidebarBox}>
              <aside>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {/* Motivational Section */}
                  <div style={{
                    background: '#e8f5e9',
                    borderLeft: '6px solid #2e7d32',
                    padding: 16,
                    borderRadius: 8
                  }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>💡 Stay Inspired</h4>
                    <p style={{ margin: 0 }}>
                      "Success is not final, failure is not fatal: It is the courage to continue that counts." – Winston Churchill
                    </p>
                  </div>

                  {/* Notice Section */}
                  <div style={{
                    background: '#fffde7',
                    borderLeft: '6px solid #fbc02d',
                    padding: 16,
                    borderRadius: 8
                  }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>⚠️ Notice</h4>
                    <p style={{ margin: 0 }}>
                      We regularly remove expired job listings to ensure you always see current and verified openings. Check back often!
                    </p>
                  </div>

                  {/* Contact Section */}
                  <div style={{
                    background: '#e3f2fd',
                    borderLeft: '6px solid #1976d2',
                    padding: 16,
                    borderRadius: 8
                  }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>📞 Need Help?</h4>
                    <p style={{ margin: 0 }}>
                      If you have questions or encounter issues, reach us at <a href="mailto:info@currentjobs.co.ke">info@currentjobs.co.ke</a>. We're here to help.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};
{/** 
const MarkdownSection = ({ title, content }) => (
  <div style={styles.section}>
    <h3>{title}</h3>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </div>
);
**/}
const MarkdownSection = ({ title, content }) => (
  <div style={{ ...styles.section, paddingRight: '12px' }}>
    <h3>{title}</h3>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ node, ...props }) => <p style={{ marginBottom: '1em' }} {...props} />,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);
const noticeStyles = (color, background) => ({
  background,
  borderLeft: `6px solid ${color}`,
  padding: 16,
  borderRadius: 8
});

const styles = {
  mainContent: {
    padding: '15px 8px', // Reduced side padding
    minHeight: '70vh',
    boxSizing: 'border-box',
  },

  contentWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '20px',
    boxSizing: 'border-box',
    width: '100%',
    // Remove maxWidth: '1200px'
  },


  leftColumn: {
    flex: 2,
    paddingRight: 20,
    minWidth: 0,
    boxSizing: 'border-box'
  },

  rightColumn: {
    flex: 1,
    paddingLeft: 20,
    marginTop: 20,
    borderLeft: '1px solid #ccc',
    boxSizing: 'border-box',
    position: 'sticky',
    top: '100px',
    alignSelf: 'flex-start'
  },


  jobContainer: {
    maxWidth: '100%',
    width: '100%',
    backgroundColor: '#fff',
    padding: '30px 24px',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    boxSizing: 'border-box'
  },
  section: {
    marginTop: 20
  },
  statusBadge: {
    color: '#fff',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: '12px',
    marginLeft: '10px'
  },
  image: {
    width: '100%',
    height: 'auto',
    borderRadius: 8,
    marginTop: 10,
    cursor: 'pointer',
    objectFit: 'contain',
    display: 'block',
    maxWidth: '100%',
  },
  pdfLink: {
    display: 'inline-block',
    marginTop: 10,
    fontWeight: 'bold',
    color: '#00796b',
    textDecoration: 'underline'
  },
  noneText: {
    fontStyle: 'italic',
    color: '#999',
    marginTop: 10
  },
  loadingContainer: {
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  errorContainer: {
    minHeight: '300px',
    textAlign: 'center',
    padding: 40
  },
  homeButton: {
    marginTop: 20,
    padding: '10px 20px',
    backgroundColor: '#00796b',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer'
  },
  sidebarBox: {
    backgroundColor: '#f5f5f5',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '12px 24px',
    marginTop: 20,
    marginBottom: 20,
    fontSize: '15px',
    lineHeight: '1.6',
  }

};

export default JobDetails;
